// biome-ignore-all lint/a11y: TODO - address a11y issues later
// biome-ignore-all lint/suspicious/noArrayIndexKey: TODO - address later
import { DndContext } from "@dnd-kit/core";
import { InfoCircledIcon } from "@radix-ui/react-icons";
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Filter, Loader2, Search, UnfoldHorizontal, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useInView } from "react-intersection-observer";
import { toast } from "sonner";
import { ActiveFiltersDisplay } from "@/components/active-filters-display";
import { ColumnAliasModal } from "@/components/column-alias-modal";
import { CSVExportButton } from "@/components/csv-export-button";
import { DraggableTableHeader } from "@/components/draggable-table-header";
import { EntityFilterModal } from "@/components/entity-filter-modal";
import { ExportFilenameDialog } from "@/components/export-filename-dialog";
import ResetTableMenu from "@/components/reset-table-menu";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { InfoTooltip } from "@/components/ui/info-tooltip";
import { Input } from "@/components/ui/input";
import { useSidebar } from "@/components/ui/sidebar";
import { Skeleton } from "@/components/ui/skeleton";
import { SortIcon } from "@/components/ui/sort-icon";
import { Table, TableBody, TableCell, TableRow } from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { WebpagesColumnSelector } from "@/components/webpages-column-selector";
import { useSavedViewContext } from "@/contexts/saved-view-context";
import { useFieldDefinitions } from "@/hooks/api/use-field-definitions";
import type { WebsitePageMetrics } from "@/hooks/api/use-website-page-metrics";
import {
  useInfiniteWebsitePageSearch,
  useWebpagesExport,
  type WebsitePage,
  type WebsitePageWithMetrics,
} from "@/hooks/api/use-website-page-search";
import { useOrganizationSessionStorage } from "@/hooks/use-browser-storage";
import { useWebpageColumnSelection } from "@/hooks/use-column-selection";
import { usePaginatedCSVExport } from "@/hooks/use-csv-export";
import { useWebpageCustomFieldSelection } from "@/hooks/use-custom-field-selection";
import { useMetricCardFilters } from "@/hooks/use-metric-card-filters";
import { usePersistedColumnState } from "@/hooks/use-persisted-column-state";
import { useSavedViewState } from "@/hooks/use-saved-view-state";
import { useSearchState } from "@/hooks/use-search-state";
import { useWebpageFilterState } from "@/hooks/use-webpage-filter-state";
import {
  useWebpageListFilters,
  useWebpageMetrics,
} from "@/hooks/use-webpage-list-filters";
import {
  constrainedClosestCenter,
  restrictToTableBounds,
} from "@/lib/collision-detection";
import type { ColumnAliases } from "@/lib/column-aliases";
import {
  createBulkColumnResizeHandler,
  createColumnResizeHandler,
  createSingleColumnResizeHandler,
} from "@/lib/column-resize-utils";
import { formatDateForFilename } from "@/lib/date-formatting";
import { cn } from "@/lib/utils";
import { MetricDrilldown } from "@/pages/webpages/webpage-explorer/metric-drilldown";
import {
  DRILLDOWN_LIMIT,
  METRIC_CONFIG,
} from "@/pages/webpages/webpage-explorer/webpage-metrics-config";
import type { CustomFieldDefinition } from "@/types/custom-fields";
import type { SortDirection, UnifiedTableColumn } from "@/types/table-column";
import { getWebpageColumnDefs, getWebpageColumns } from "./webpage-columns";

export function WebpagesDataTable() {
  const WEBPAGE_METRIC_LIMIT = 20000;

  // Use the sidebar hook to get state
  const { state, isMobile } = useSidebar();
  const { isLoadingView, loadedViewId } = useSavedViewContext();
  const { resetSpecificKeys } = useSavedViewState({
    pageType: "webpages::list",
  });

  // State for sorting and searching - Replace useState search with sessionStorage
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: SortDirection;
  }>({
    key: "url",
    direction: "asc",
  });

  // Use sessionStorage for search state (tab-specific)
  const {
    searchInputValue,
    debouncedSearchQuery,
    handleSearchChange,
    clearSearch,
  } = useSearchState("webpages::list::search");

  const [showFilterModal, setShowFilterModal] = useState(false);
  const [showPerformanceMessage, setShowPerformanceMessage] = useState(false);
  const [selectedChannelId, setSelectedChannelId] = useState<string>("all");
  const [selectedDrilldown, setSelectedDrilldown] = useState<{
    webpageUrl: string;
    metric: keyof WebsitePageMetrics;
  } | null>(null);

  // Column aliases state management using sessionStorage (no longer persisted across sessions)
  const [columnAliases, setColumnAliases] =
    useOrganizationSessionStorage<ColumnAliases>(
      "webpages::list::columns::aliases",
      {},
      {
        // Migrate from localStorage to sessionStorage
        migration: {
          from: {
            key: "webpages::list::columns::aliases",
            storage: "localStorage",
            organizationScoped: true,
          },
          cleanup: true, // Remove from localStorage after migration
        },
      },
    );
  const [showAliasModal, setShowAliasModal] = useState(false);
  const [aliasColumnId, setAliasColumnId] = useState<string>("");

  // Track the previous sort config when it gets reset due to API warning
  const previousSortConfigRef = useRef<{
    key: string;
    direction: SortDirection;
  } | null>(null);

  // Ref to track if a metric sort was cleared by 'Clear All'
  const metricSortClearedRef = useRef<string | null>(null);

  // Loading refs for infinite scroll
  const { ref: loadMoreRef, inView } = useInView();

  // Structured filter groups (account / opportunity / touchpoint)
  const webpageFilterState = useWebpageFilterState("webpages::list");
  const {
    accountFilters,
    opportunityFilters,
    touchpointFilters,
    hasAccountFilters,
    hasOpportunityFilters,
    hasTouchpointFilters,
    clearAllFilters: clearAllMetricFilters,
  } = webpageFilterState;

  // Total count of active metric filters (account + opportunity + touchpoint)
  const totalMetricFilterCount = useMemo(
    () =>
      accountFilters.filters.length +
      opportunityFilters.filters.length +
      touchpointFilters.filters.length,
    [accountFilters, opportunityFilters, touchpointFilters],
  );

  // Process metric filters for display using the same logic as Report Cards
  const {
    displayFilters: metricDisplayFilters,
    filterColumns: metricFilterColumns,
    handleClearFilter: handleClearMetricFilter,
  } = useMetricCardFilters(webpageFilterState);

  // Use webpage list filters hook
  const {
    filters,
    updateFilter,
    clearFilter,
    clearAllFilters,
    hasFilters,
    getDefaultSortSettings,
    getQueryParams,
  } = useWebpageListFilters();

  // Use the new webpage metrics hook
  const { selectedMetrics, setSelectedMetrics, getDefaultMetrics } =
    useWebpageMetrics();

  // Use custom field selection hook
  const { selectedFieldsList } = useWebpageCustomFieldSelection();

  // Add missing state variables
  const [isMetricLimitExceeded, setIsMetricLimitExceeded] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const tableContainerRef = useRef<HTMLDivElement>(null);

  // Reset sort to defaults
  const resetSortToDefaults = useCallback(() => {
    const defaults = getDefaultSortSettings();
    setSortConfig({
      key: defaults.sortColumn,
      direction: defaults.sortDirection,
    });
  }, [getDefaultSortSettings]);

  // Handle metric click to open drill-down
  const handleMetricClick = useCallback(
    (webpageUrl: string, metric: keyof WebsitePageMetrics) => {
      setSelectedDrilldown({ webpageUrl, metric });
    },
    [],
  );

  // Fetch unified field definitions for webpages
  const { data: fieldDefs } = useFieldDefinitions("webpages");

  // Derive select options for standard enum fields from field definitions
  const filterOptions = useMemo(() => {
    if (!fieldDefs) return undefined;

    // Build a map of enum values for all fields with enums
    const enumMap = fieldDefs.reduce<Record<string, string[]>>((acc, field) => {
      if (field.has_enum_values && field.enum_values) {
        acc[field.id] = field.enum_values;
      }
      return acc;
    }, {});

    return {
      page_type: enumMap.page_type || [],
    } as const;
  }, [fieldDefs]);

  // Define columns for the table, including metric columns
  const columns = useMemo<UnifiedTableColumn<WebsitePage>[]>(
    () =>
      getWebpageColumns(
        filterOptions,
        selectedMetrics,
        [],
        selectedFieldsList,
        handleMetricClick,
      ),
    [filterOptions, selectedMetrics, selectedFieldsList, handleMetricClick],
  );

  // Get column selection hook to control visibility
  const {
    getVisibleColumnDefs,
    columnVisibility,
    setColumnVisibility,
    selectedCustomColumns,
    resetToDefault: baseResetColumnVisibility,
  } = useWebpageColumnSelection(columns, selectedMetrics);

  // Reset column visibility **and** metric selection to defaults
  const resetColumnVisibility = useCallback(() => {
    baseResetColumnVisibility();
    setSelectedMetrics(getDefaultMetrics());
  }, [baseResetColumnVisibility, setSelectedMetrics, getDefaultMetrics]);

  const handleSelectMetrics = useCallback(
    (metrics: Array<keyof WebsitePageMetrics>) => {
      // Check if any removed metrics are currently used for sorting
      const removedMetrics = selectedMetrics.filter(
        (metric) => !metrics.includes(metric),
      );

      // Check if the current sort is on a metric that's being removed
      if (sortConfig.key.startsWith("metric_")) {
        const currentMetricKey = sortConfig.key.replace(
          "metric_",
          "",
        ) as keyof WebsitePageMetrics;

        if (removedMetrics.includes(currentMetricKey)) {
          // Find the metric title from the config
          const metricConfig = METRIC_CONFIG[currentMetricKey];
          const metricTitle = metricConfig?.title || currentMetricKey;

          // Show toast warning
          toast.warning("Table sort reset", {
            description: `Sort on ${metricTitle} has been removed because the column was deselected.`,
            duration: 8000,
          });

          // Reset sort to defaults
          resetSortToDefaults();
          clearFilter("sortColumn");
          clearFilter("sortDirection");
        }
      }

      setSelectedMetrics(metrics);
    },
    [
      selectedMetrics,
      sortConfig.key,
      setSelectedMetrics,
      resetSortToDefaults,
      clearFilter,
    ],
  );

  // Handle clear all filters and reset sort - Updated for sessionStorage
  const handleClearAllFilters = useCallback(() => {
    // Check if the current sort is a metric sort before clearing
    const currentSortKey = sortConfig.key;
    if (currentSortKey.startsWith("metric_")) {
      // Mark that a metric sort was cleared
      metricSortClearedRef.current = currentSortKey;
    }

    // Clear all filters
    clearAllFilters();

    // Clear search state from sessionStorage
    clearSearch();

    // Clear channel filter
    setSelectedChannelId("all");

    // Get default sort and update UI state
    const defaults = getDefaultSortSettings();
    setSortConfig({
      key: defaults.sortColumn,
      direction: defaults.sortDirection,
    });

    // After clearing all filters, add ONLY the default sort back to filters
    // to ensure it's included in API requests
    updateFilter("sortColumn", defaults.sortColumn);
    updateFilter("sortDirection", defaults.sortDirection);
  }, [
    clearAllFilters,
    getDefaultSortSettings,
    updateFilter,
    sortConfig.key, // Clear search state from sessionStorage
    clearSearch,
  ]);

  // When component mounts, set search and sort from filters if they exist
  useEffect(() => {
    // Set sort configuration from filters (search is now in sessionStorage)
    if (filters.sortColumn) {
      // If sort is set in filters, use that
      setSortConfig({
        key: filters.sortColumn as string,
        direction: filters.sortDirection as SortDirection,
      });
    } else {
      // No sort in filters, use defaults
      const defaults = getDefaultSortSettings();
      setSortConfig({
        key: defaults.sortColumn,
        direction: defaults.sortDirection,
      });

      // When initializing with defaults and no sort in filters, add sort to filters
      updateFilter("sortColumn", defaults.sortColumn);
      updateFilter("sortDirection", defaults.sortDirection);
    }
  }, [
    filters.sortColumn,
    filters.sortDirection,
    getDefaultSortSettings,
    updateFilter,
  ]);

  // Ref to track previously visible column IDs
  const prevActiveColumnsRef = useRef<string[]>([]);

  // Current visible column ids (core + custom)
  const activeColumnIds = useMemo(() => {
    const visibleCore = Object.entries(columnVisibility)
      .filter(([, v]) => v)
      .map(([id]) => id);

    const customIds = selectedCustomColumns.map((c) => c.id);

    return Array.from(new Set([...visibleCore, ...customIds]));
  }, [columnVisibility, selectedCustomColumns]);
  // Persist column titles to use after removal
  const columnTitleLookupRef = useRef<Record<string, string>>({});

  useEffect(() => {
    const lookup = columnTitleLookupRef.current;
    columns.forEach((col) => {
      const key = col.key.toString();
      if (!lookup[key]) {
        lookup[key] = col.title;
      }
    });
  }, [columns]);

  useEffect(() => {
    const prev = prevActiveColumnsRef.current;
    const removedIds = prev.filter((id) => !activeColumnIds.includes(id));

    if (removedIds.length > 0) {
      removedIds.forEach((id) => {
        if (filters[id] !== undefined) {
          clearFilter(id);
        }

        // Only reset sort if we're not currently loading a saved view
        if (sortConfig.key === id && !isLoadingView) {
          resetSortToDefaults();
          clearFilter("sortColumn");
          clearFilter("sortDirection");

          // Try to get column title from current columns first, then fallback to lookup
          let columnTitle = columnTitleLookupRef.current[id];

          if (!columnTitle) {
            // Try to find the column in the current columns array
            const column = columns.find((col) => col.key.toString() === id);
            columnTitle = column?.title || "";
          }

          if (columnTitle) {
            toast.warning("Table sort reset", {
              description: `Sort on ${columnTitle} has been removed because the column was deselected.`,
              duration: 8000,
            });
          }
        }
      });
    }

    prevActiveColumnsRef.current = activeColumnIds;
  }, [
    activeColumnIds,
    filters,
    sortConfig,
    clearFilter,
    resetSortToDefaults,
    columns,
    isLoadingView,
  ]);

  // Memoize deduplication of custom fields to avoid recreating on every render
  const uniqueCustomFields = useMemo(() => {
    return [
      ...selectedFieldsList,
      ...selectedCustomColumns
        .filter((column) => column.customField)
        .map((column) => column.customField!),
    ].reduce((map, field) => {
      map.set(field.id, field);
      return map;
    }, new Map<string, CustomFieldDefinition>());
  }, [selectedFieldsList, selectedCustomColumns]);

  // Build query parameters for API call
  const queryParams = useMemo(() => {
    const params = getQueryParams({
      searchTerm: debouncedSearchQuery,
      columns: columns,
      limit: 25,
      metrics: selectedMetrics.length > 0 ? selectedMetrics : undefined,
      metric_filters: {
        ...(hasAccountFilters && {
          account_filters: accountFilters,
        }),
        ...(hasOpportunityFilters && {
          opportunity_filters: opportunityFilters,
        }),
        ...(hasTouchpointFilters && {
          touchpoint_filters: touchpointFilters,
        }),
      },
    });

    // Add channelId to params
    return {
      ...params,
      channelId: selectedChannelId !== "all" ? selectedChannelId : undefined,
    };
  }, [
    getQueryParams,
    debouncedSearchQuery,
    columns,
    selectedChannelId,
    selectedMetrics,
    hasAccountFilters,
    hasOpportunityFilters,
    hasTouchpointFilters,
    accountFilters,
    opportunityFilters,
    touchpointFilters,
  ]);

  // Fetch webpages with infinite scrolling
  const {
    data,
    isLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isError,
    error,
  } = useInfiniteWebsitePageSearch(queryParams);

  // Setup for CSV export
  const { exportWebpagesAsync } = useWebpagesExport();

  // Helper function to build query parameters (shared between main query and CSV export)
  const buildQueryParams = useCallback(
    (limit: number) => {
      const baseParams = {
        ...getQueryParams({
          searchTerm: debouncedSearchQuery,
          columns: columns,
          limit,
          metrics: selectedMetrics.length > 0 ? selectedMetrics : undefined,
          metric_filters: {
            ...(hasAccountFilters && {
              account_filters: accountFilters,
            }),
            ...(hasOpportunityFilters && {
              opportunity_filters: opportunityFilters,
            }),
            ...(hasTouchpointFilters && {
              touchpoint_filters: touchpointFilters,
            }),
          },
        }),
        channelId: selectedChannelId !== "all" ? selectedChannelId : undefined,
      };

      return baseParams;
    },
    [
      getQueryParams,
      debouncedSearchQuery,
      columns,
      selectedMetrics,
      hasAccountFilters,
      hasOpportunityFilters,
      hasTouchpointFilters,
      accountFilters,
      opportunityFilters,
      touchpointFilters,
      selectedChannelId,
    ],
  );

  // Set up paginated CSV export
  const { exportData, isExporting, defaultFilename } =
    usePaginatedCSVExport<WebsitePageWithMetrics>({
      fetchAllData: async () => {
        // Use the same parameter building logic as the main query
        const baseParams = buildQueryParams(25);
        return exportWebpagesAsync(baseParams);
      },
      columns: columns as UnifiedTableColumn<WebsitePageWithMetrics>[],
      filename: `webpages_${formatDateForFilename(new Date())}`,
    });

  // State for export filename dialog
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);

  const handleExport = (filename: string) => {
    exportData(filename);
  };

  // Show performance message after 5 seconds of loading
  useEffect(() => {
    if (isLoading && selectedMetrics.length > 0) {
      const timer = setTimeout(() => {
        setShowPerformanceMessage(true);
      }, 5000); // 5 seconds

      return () => clearTimeout(timer);
    } else {
      setShowPerformanceMessage(false);
    }
  }, [isLoading, selectedMetrics.length]);

  // Function to display the sort reset toast notification
  const showSortResetToast = useCallback(
    (columnTitle: string, customDescription?: string) => {
      const description =
        customDescription ||
        `For performance reasons, the sort on ${columnTitle} has been removed. Please filter to fewer than ${WEBPAGE_METRIC_LIMIT} webpages and then try re-applying the sort.`;

      toast.warning("Table sort reset", {
        description,
        duration: 10000,
      });
    },
    [],
  );

  // Effect to show toast when a metric sort is cleared via 'Clear All'
  useEffect(() => {
    if (metricSortClearedRef.current) {
      const clearedMetricKey = metricSortClearedRef.current;
      // Find the column title using the key stored in the ref
      const column = columns.find((col) => col.key === clearedMetricKey);

      if (column?.title) {
        // Call the consolidated toast function
        showSortResetToast(
          column.title,
          "Sort on metric has been removed because the column was deselected.",
        );
      }
      // Reset the ref after showing the toast
      metricSortClearedRef.current = null;
    }
  }, [columns, showSortResetToast]);

  // Track when a metric sort warning appears and reset sort (API driven)
  useEffect(() => {
    const firstPage = data?.pages[0];
    if (firstPage?.client_warning === "SORT_ON_METRIC_TOO_MANY_MATCHING") {
      // Store the current sort config before resetting it
      if (sortConfig.key.startsWith("metric_")) {
        previousSortConfigRef.current = { ...sortConfig };
        // Reset sort to default in the UI
        resetSortToDefaults();

        // Clear sort from filters
        clearFilter("sortColumn");
        clearFilter("sortDirection");

        // Find the column to get the title
        const column = columns.find(
          (col) => col.key === previousSortConfigRef.current?.key,
        );

        if (column?.title) {
          // Call the consolidated toast function
          showSortResetToast(column.title);
        }
      }
    }
  }, [
    sortConfig,
    resetSortToDefaults,
    clearFilter,
    columns,
    showSortResetToast,
    data?.pages,
  ]);

  // Monitor for API warnings separately to avoid circular dependencies
  useEffect(() => {
    const firstPage = data?.pages[0];
    // This effect only monitors data changes for warnings
    if (firstPage?.client_warning === "SORT_ON_METRIC_TOO_MANY_MATCHING") {
      // Warning handling is done in the previous effect
    }
  }, [data]);

  // Flatten the pages of data into a single array
  const webpages = useMemo(() => {
    return data?.pages.flatMap((page) => page.results) || [];
  }, [data]);

  const {
    columnOrder,
    columnSizing,
    setColumnSizing,
    pinnedColumns,
    toggleColumnPin,
    wrappedColumns,
    setWrappedColumns,
    toggleColumnWrap,
    dndState: { sensors, handleDragEnd },
    setColumnOrder,
    resetToDefaultPinned,
    getInitializeColumnOrder,
  } = usePersistedColumnState("webpages::list::columns", {
    getDefaultPinnedColumns: () => ["url"],
  });

  // Function to handle double-click on column resize handle to auto-size
  const handleDoubleClickResize = useCallback(
    createColumnResizeHandler(setColumnSizing, "webpages-table"),
    [],
  );

  // Single column resize handler for kebab menu
  const handleSingleColumnResize = useCallback(
    createSingleColumnResizeHandler(setColumnSizing, "webpages-table"),
    [],
  );

  // Bulk resize handler for autosize all button
  const handleBulkColumnResize = useCallback(
    createBulkColumnResizeHandler(setColumnSizing, "webpages-table"),
    [],
  );

  // Handle removing a column from view
  const handleRemoveColumn = useCallback(
    (columnId: string) => {
      // Find the column to check its type
      const column = columns.find((col) => col.key === columnId);

      // For metric columns, remove from selectedMetrics
      if (column?.columnType === "metric") {
        // Extract metric key from column ID following our naming convention
        // Column IDs are prefixed for uniqueness: "metric_accounts_engaged"
        // But selectedMetrics state and API expect unprefixed keys: "accounts_engaged"
        const metricKey = columnId.replace(
          "metric_",
          "",
        ) as keyof WebsitePageMetrics;
        setSelectedMetrics((prev) => prev.filter((m) => m !== metricKey));
      } else {
        // For all other columns (core, custom, formula), update visibility
        setColumnVisibility((prev) => ({
          ...prev,
          [columnId]: false,
        }));
      }
    },
    [columns, setColumnVisibility, setSelectedMetrics],
  );

  // Alias handlers
  const handleSetAlias = useCallback((columnId: string) => {
    setAliasColumnId(columnId);
    setShowAliasModal(true);
  }, []);

  const handleSaveAlias = useCallback(
    (columnId: string, alias: string) => {
      setColumnAliases((prev) => ({ ...prev, [columnId]: alias }));
    },
    [setColumnAliases],
  );

  const handleClearAlias = useCallback(
    (columnId: string) => {
      setColumnAliases((prev) => {
        const newAliases = { ...prev };
        delete newAliases[columnId];
        return newAliases;
      });
    },
    [setColumnAliases],
  );

  const handleResetAliases = useCallback(() => {
    setColumnAliases({});
  }, [setColumnAliases]);

  // Convert to TanStack columnDefs with resizing enabled
  const columnDefs = useMemo<ColumnDef<WebsitePage>[]>(() => {
    // Get column definitions with custom fields
    const allDefs = getWebpageColumnDefs(
      filterOptions,
      selectedMetrics,
      [],
      Array.from(uniqueCustomFields.values()),
      handleMetricClick,
    );

    // Add resizing capability and filter to only show visible columns
    return getVisibleColumnDefs(
      allDefs.map((col) => ({ ...col, enableResizing: true })),
    );
  }, [
    filterOptions,
    uniqueCustomFields,
    getVisibleColumnDefs,
    selectedMetrics,
    handleMetricClick,
  ]);

  // Initialize TanStack Table
  const table = useReactTable({
    data: webpages,
    columns: columnDefs,
    state: {
      columnSizing,
      columnOrder,
    },
    onColumnSizingChange: setColumnSizing,
    columnResizeMode: "onChange",
    getCoreRowModel: getCoreRowModel(),
    defaultColumn: {
      minSize: 80,
      size: 150,
    },
  });

  // Reset column order only (preserve pinned columns at the start)
  const resetColumnOrder = useCallback(() => {
    setColumnOrder((prev) => {
      const defaultOrder = columnDefs.map((c) => c.id!);
      const pinnedFirst = prev.filter((id) => pinnedColumns.includes(id));
      const unpinnedDefault = defaultOrder.filter(
        (id) => !pinnedColumns.includes(id),
      );
      return [...pinnedFirst, ...unpinnedDefault];
    });
  }, [setColumnOrder, columnDefs, pinnedColumns]);

  // Reset pinned columns - respects saved views if loaded
  const resetPinnedColumns = useCallback(() => {
    if (loadedViewId) {
      // Reset to saved view's configuration
      resetSpecificKeys([
        "webpages::list::columns::pinned",
        "webpages::list::columns::pinned-original-positions",
      ]);
    } else {
      // No saved view - reset to default pinned state
      resetToDefaultPinned();
    }
  }, [loadedViewId, resetSpecificKeys, resetToDefaultPinned]);

  // Reset wrapped columns
  const resetWrappedColumns = useCallback(() => {
    setWrappedColumns([]);
  }, [setWrappedColumns]);

  // Reset sort order
  const resetSortOrder = useCallback(() => {
    const defaults = getDefaultSortSettings();
    setSortConfig({
      key: defaults.sortColumn,
      direction: defaults.sortDirection,
    });
    updateFilter("sortColumn", defaults.sortColumn);
    updateFilter("sortDirection", defaults.sortDirection);
  }, [getDefaultSortSettings, updateFilter]);

  // Initialize column order when columnDefs change
  useEffect(() => {
    const initializeOrder = getInitializeColumnOrder(columnDefs);
    initializeOrder();
  }, [columnDefs, getInitializeColumnOrder]);

  // Reset column widths only
  const resetColumnWidths = useCallback(() => {
    setColumnSizing({});
  }, [setColumnSizing]);

  // Comprehensive table reset function
  const handleResetTable = useCallback(() => {
    // Clear filters and sorting
    handleClearAllFilters();

    // Reset column visibility to defaults
    resetColumnVisibility();

    // Reset metric filters
    clearAllMetricFilters();

    // Reset column order and widths
    resetColumnOrder();
    resetColumnWidths();

    // Reset pinned columns
    resetPinnedColumns();

    // Reset column aliases
    setColumnAliases({});

    // Reset wrapped columns
    resetWrappedColumns();

    // Table state clearing is handled by organization-scoped storage hooks

    // Show success toast
    toast.success("Table reset to default state");
  }, [
    handleClearAllFilters,
    resetColumnVisibility,
    clearAllMetricFilters,
    resetColumnOrder,
    resetColumnWidths,
    resetPinnedColumns,
    setColumnAliases,
    resetWrappedColumns,
  ]);

  // Update total count and metric limit status when data changes
  useEffect(() => {
    if (data?.pages && data.pages.length > 0) {
      // Use the total from the first page response if available
      const firstPage = data.pages[0];
      if (firstPage.total !== undefined) {
        setTotalCount(firstPage.total);
        setIsMetricLimitExceeded(firstPage.total > WEBPAGE_METRIC_LIMIT);
      } else {
        // Fallback: calculate based on loaded items if total not provided
        const loadedCount = data.pages.reduce(
          (sum, page) => sum + page.results.length,
          0,
        );
        setTotalCount(loadedCount);
        setIsMetricLimitExceeded(loadedCount > WEBPAGE_METRIC_LIMIT);
      }
    }
  }, [data]);

  // Load more data when the load more element is in view
  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Search is now managed by useSearchState hook - no need for manual debouncing

  // Handle sorting
  const handleSort = async (colId: string) => {
    // Find the original UnifiedTableColumn definition for default sort direction
    const unifiedColumn = columns.find((col) => col.key.toString() === colId);
    const sortable = unifiedColumn?.sortable;

    if (!sortable) return;

    const isMetricColumn = colId.startsWith("metric_");

    let direction: SortDirection = unifiedColumn?.defaultSortDirection || "asc";

    if (sortConfig.key === colId) {
      // Toggle between asc and desc only
      direction = sortConfig.direction === "asc" ? "desc" : "asc";
    }

    // Skip sorting on metric columns when metric limit is exceeded
    if (isMetricColumn && isMetricLimitExceeded) return;

    // Update filters state for server-side sorting
    updateFilter("sortColumn", colId);
    updateFilter("sortDirection", direction);

    // Update UI state for sort icons
    setSortConfig({ key: colId, direction });
  };

  // Search functions are now provided by useSearchState hook

  // Render sort icon
  const renderSortIcon = useCallback(
    (column: UnifiedTableColumn<WebsitePage>) => {
      if (!column.sortable) return null;

      const key = column.key.toString();

      // Check if this is a metric column and if sorting is disabled due to limit
      const isMetricColumn = key.startsWith("metric_");

      // For metric columns, hide icon if limit exceeded
      if (isMetricColumn && isMetricLimitExceeded) {
        return null;
      }

      return (
        <SortIcon
          isActive={sortConfig.key === key}
          direction={sortConfig.key === key ? sortConfig.direction : null}
          showOnHover={false} // Don't show the icon on hover, only when active
        />
      );
    },
    [sortConfig, isMetricLimitExceeded],
  );

  // Helper function to define the tooltipText to be used in both places
  const getMetricLimitTooltipText = () => {
    return `Apply additional filters to sort on metrics. For performance reasons, sorting on metrics is currently disabled when more than ${WEBPAGE_METRIC_LIMIT} webpages match filters.`;
  };

  // Render loading state
  const renderLoadingState = () => {
    return (
      <>
        <DraggableTableHeader
          table={table}
          columnOrder={columnOrder}
          handleSort={handleSort}
          sortConfig={sortConfig}
          renderSortIcon={renderSortIcon}
          filters={filters}
          updateFilter={updateFilter}
          clearFilter={clearFilter}
          columns={columns}
          handleDoubleClickResize={handleDoubleClickResize}
          isMetricLimitExceeded={isMetricLimitExceeded}
          getMetricLimitTooltipText={getMetricLimitTooltipText}
          pinnedColumns={pinnedColumns}
          onTogglePin={toggleColumnPin}
          wrappedColumns={wrappedColumns}
          onToggleWrap={toggleColumnWrap}
          onResizeToFit={handleSingleColumnResize}
          onRemoveColumn={handleRemoveColumn}
          columnAliases={columnAliases}
          onSetAlias={handleSetAlias}
        />
        <TableBody className="bg-card">
          {Array.from({ length: 7 }).map((_, i) => (
            <TableRow key={i}>
              {i === 3 && showPerformanceMessage ? (
                <TableCell
                  colSpan={table.getAllColumns().length}
                  className="py-6"
                >
                  <div className="flex items-center">
                    <div
                      role="status"
                      aria-live="polite"
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-muted/50 border border-muted-foreground/10 animate-in fade-in duration-500"
                    >
                      <svg
                        className="w-4 h-4 text-primary flex-shrink-0"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13 10V3L4 14h7v7l9-11h-7z"
                        />
                      </svg>
                      <span className="text-sm font-medium text-muted-foreground">
                        To speed up query time, try adding metric filters or
                        reducing the number of metric columns
                      </span>
                    </div>
                  </div>
                </TableCell>
              ) : (
                table.getAllColumns().map((column) => (
                  <TableCell key={`${i}-${column.id}`}>
                    <Skeleton className="h-4 w-full" />
                  </TableCell>
                ))
              )}
            </TableRow>
          ))}
        </TableBody>
      </>
    );
  };

  // Generate export button tooltip based on conditions
  const exportTooltipText = useMemo(() => {
    if (isMetricLimitExceeded && selectedMetrics.length > 0) {
      return `To maintain performance, exporting metrics for more than ${WEBPAGE_METRIC_LIMIT} webpages is not currently supported. Please filter to less than ${WEBPAGE_METRIC_LIMIT} webpages, or remove metric columns in order to export`;
    }
    return "Export webpages data to CSV";
  }, [isMetricLimitExceeded, selectedMetrics.length]);

  // Determine if export should be disabled
  const isExportDisabled = useMemo(() => {
    return (
      isExporting ||
      isLoading ||
      webpages.length === 0 ||
      (isMetricLimitExceeded && selectedMetrics.length > 0)
    );
  }, [
    isExporting,
    isLoading,
    webpages.length,
    isMetricLimitExceeded,
    selectedMetrics.length,
  ]);

  // Memoize pinnedColumnPositions calculation at component level
  const pinnedColumnPositions = useMemo(() => {
    const positions: Record<string, number> = {};
    let cumulativeLeft = 0;

    columnOrder.forEach((colId) => {
      if (pinnedColumns.includes(colId)) {
        positions[colId] = cumulativeLeft;
        const column = table.getColumn(colId);
        if (column) {
          cumulativeLeft += column.getSize();
        }
      }
    });

    return positions;
  }, [columnOrder, pinnedColumns, table]);

  // Render table content (headers, body, etc.)
  const renderTableContent = () => {
    const rowModel = table.getRowModel();

    return (
      <>
        <DraggableTableHeader
          table={table}
          columnOrder={columnOrder}
          handleSort={handleSort}
          sortConfig={sortConfig}
          renderSortIcon={renderSortIcon}
          filters={filters}
          updateFilter={updateFilter}
          clearFilter={clearFilter}
          columns={columns}
          handleDoubleClickResize={handleDoubleClickResize}
          isMetricLimitExceeded={isMetricLimitExceeded}
          getMetricLimitTooltipText={getMetricLimitTooltipText}
          pinnedColumns={pinnedColumns}
          onTogglePin={toggleColumnPin}
          wrappedColumns={wrappedColumns}
          onToggleWrap={toggleColumnWrap}
          onResizeToFit={handleSingleColumnResize}
          onRemoveColumn={handleRemoveColumn}
          columnAliases={columnAliases}
          onSetAlias={handleSetAlias}
        />
        <TableBody className="bg-card">
          {isError ? (
            <TableRow>
              <TableCell
                colSpan={table.getAllColumns().length}
                className="h-24 text-center text-muted-foreground"
              >
                Error loading web pages: {error.message}
              </TableCell>
            </TableRow>
          ) : webpages.length === 0 && !isLoading ? (
            <TableRow>
              <TableCell
                colSpan={table.getAllColumns().length}
                className="h-24 text-center text-muted-foreground"
              >
                No matching web pages.
              </TableCell>
            </TableRow>
          ) : (
            <>
              {rowModel.rows.map((row) => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => {
                    const isPinned = pinnedColumns.includes(cell.column.id);
                    const isWrapped = wrappedColumns.includes(cell.column.id);
                    const pinnedLeft = pinnedColumnPositions[cell.column.id];

                    return (
                      <TableCell
                        key={cell.id}
                        style={{
                          width: cell.column.getSize(),
                          minWidth: cell.column.getSize(),
                          maxWidth: cell.column.getSize(),
                          ...(isPinned && { left: `${pinnedLeft}px` }),
                        }}
                        className={cn(
                          cell.column.id === sortConfig.key && "bg-muted/40",
                          isPinned && "pinned-column bg-background",
                        )}
                        data-column-id={cell.column.id}
                      >
                        <div
                          className={cn("cell-content", isWrapped && "wrapped")}
                          title={String(row.getValue(cell.column.id) || "")}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </div>
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}

              {/* Load more trigger element */}
              <TableRow ref={loadMoreRef}>
                <TableCell
                  colSpan={table.getAllColumns().length}
                  className={cn("h-12", !hasNextPage && "hidden")}
                >
                  {!isFetchingNextPage && hasNextPage ? "Load more" : null}
                </TableCell>
              </TableRow>
            </>
          )}
        </TableBody>
      </>
    );
  };

  // Check if the current sort is on a metric column that's been removed
  useEffect(() => {
    if (
      sortConfig.key.startsWith("metric_") &&
      !selectedMetrics.includes(
        sortConfig.key.replace("metric_", "") as keyof WebsitePageMetrics,
      )
    ) {
      // Reset sort to defaults if sorting on a metric that was removed
      resetSortToDefaults();

      // Also clear the sort from filters
      clearFilter("sortColumn");
      clearFilter("sortDirection");
    }
  }, [selectedMetrics, sortConfig.key, resetSortToDefaults, clearFilter]);

  return (
    <Card className="h-full relative flex flex-col">
      {/* Fixed position loading indicator */}
      {isFetchingNextPage && (
        <div className="fixed bottom-10 left-1/2 transform -translate-x-1/2 z-10 bg-background/90 shadow-md rounded-md px-4 py-2 border">
          <div className="flex items-center justify-center">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="ml-2">Loading more...</span>
          </div>
        </div>
      )}
      <div className="p-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-0 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Filter by URL..."
              className="w-[250px] border-0 border-b border-border shadow-none rounded-none pl-7 pr-8 h-10 placeholder:text-muted-foreground focus:ring-0 focus-visible:ring-0 focus:border-muted-foreground transition-colors bg-transparent hover:border-muted-foreground"
              onChange={(e) => handleSearchChange(e.target.value)}
              value={searchInputValue}
            />
            {searchInputValue && (
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-0 top-1/2 -translate-y-1/2 h-6 w-6 p-0 hover:bg-transparent"
                onClick={clearSearch}
              >
                <X className="h-4 w-4 text-muted-foreground hover:text-foreground/80" />
              </Button>
            )}
          </div>
          <InfoTooltip
            trigger={
              <button
                className="ml-2 text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Wildcard search info"
                onClick={(e) => e.stopPropagation()}
              >
                <InfoCircledIcon className="h-3.5 w-3.5" />
              </button>
            }
            side="right"
            sideOffset={5}
          >
            <div className="text-sm space-y-2">
              <p className="font-medium">Supports wildcards</p>
              <ul className="space-y-1">
                <li>
                  <code className="px-1 py-0.5 bg-muted rounded text-xs">
                    _
                  </code>{" "}
                  matches a single character
                </li>
                <li>
                  <code className="px-1 py-0.5 bg-muted rounded text-xs">
                    %
                  </code>{" "}
                  matches zero or more characters
                </li>
              </ul>
            </div>
          </InfoTooltip>
        </div>
        <div className="flex items-center gap-2">
          <div className="text-sm text-muted-foreground">
            {isLoading
              ? "Loading..."
              : totalCount > 0
                ? `${totalCount.toLocaleString()} matching web pages`
                : "No matching web pages"}
          </div>
          {/* Unified Webpages Column & Metrics Selector */}
          <WebpagesColumnSelector
            selectedMetrics={selectedMetrics}
            onSelectMetrics={handleSelectMetrics}
            showMetricsSection={true}
            defaultMetrics={getDefaultMetrics()}
          />

          {/* Metric Filters Button - Only show when metrics are selected */}
          {selectedMetrics.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFilterModal(true)}
              className="flex items-center gap-1.5"
            >
              <Filter
                className={`h-4 w-4 ${
                  hasAccountFilters || hasOpportunityFilters
                    ? "text-primary"
                    : "text-muted-foreground"
                }`}
              />
              <span>Metric Filters</span>
              {totalMetricFilterCount > 0 && (
                <Badge className="ml-1 bg-primary text-primary-foreground">
                  {totalMetricFilterCount}
                </Badge>
              )}
            </Button>
          )}

          {/* Autosize All Columns Button */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                onClick={() => {
                  const visibleColumnIds = columnOrder.filter(
                    (columnId) => columnVisibility[columnId] !== false,
                  );
                  handleBulkColumnResize(visibleColumnIds);
                }}
                className="h-8 w-8"
              >
                <UnfoldHorizontal className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Resize all visible columns to fit their content</p>
            </TooltipContent>
          </Tooltip>

          {/* Reset Table Menu */}
          <ResetTableMenu
            onResetWidths={resetColumnWidths}
            onResetOrder={resetColumnOrder}
            onResetColumns={resetColumnVisibility}
            onResetTableFilters={handleClearAllFilters}
            onResetMetricFilters={clearAllMetricFilters}
            onResetPinnedColumns={resetPinnedColumns}
            onResetSort={resetSortOrder}
            onResetAliases={handleResetAliases}
            onResetWrappedColumns={resetWrappedColumns}
            onResetAll={handleResetTable}
          />

          {/* CSV Export Button */}
          <ExportFilenameDialog
            open={isExportDialogOpen}
            onOpenChange={setIsExportDialogOpen}
            defaultFilename={defaultFilename}
            extension="csv"
            onExport={handleExport}
            disabled={isExportDisabled}
          >
            <CSVExportButton
              onClick={() => setIsExportDialogOpen(true)}
              isExporting={isExporting}
              disabled={isExportDisabled}
              tooltipText={exportTooltipText}
            />
          </ExportFilenameDialog>
        </div>
      </div>

      {/* Filter Modal */}
      <EntityFilterModal
        open={showFilterModal}
        onOpenChange={setShowFilterModal}
        scope="webpages::list"
        entity="webpage"
      />

      {/* Active Filters Display - Show both table-level and metric filters */}
      {/* Active Filters Display – fixed 32px height */}
      <div className="px-4 pb-2 space-y-2 min-h-8 flex-shrink-0">
        {/* Table-level filters */}
        {hasFilters && (
          <div className="flex items-center justify-between">
            <ActiveFiltersDisplay
              filters={
                // Filter out sort-related properties from display
                Object.fromEntries(
                  Object.entries(filters).filter(
                    ([key]) => key !== "sortColumn" && key !== "sortDirection",
                  ),
                )
              }
              columns={columns.map((col) => ({
                key: col.key.toString(),
                title: col.title,
                filterType: col.filterType,
                filterOptions: col.filterOptions,
              }))}
              clearFilter={clearFilter}
              clearAllFilters={handleClearAllFilters}
              clearAllText="Clear all table filters"
              filterType="table"
            />
          </div>
        )}

        {/* Metric filters */}
        {Object.keys(metricDisplayFilters).length > 0 && (
          <div className="flex items-center justify-between">
            <ActiveFiltersDisplay
              filters={metricDisplayFilters}
              columns={metricFilterColumns}
              clearFilter={handleClearMetricFilter}
              clearAllFilters={clearAllMetricFilters}
              clearAllText="Clear all metric filters"
              filterType="metric"
            />
          </div>
        )}
      </div>

      {/* Main Table Container */}
      <DndContext
        sensors={sensors}
        collisionDetection={constrainedClosestCenter}
        onDragEnd={handleDragEnd}
        modifiers={[restrictToTableBounds]}
      >
        <div
          ref={tableContainerRef}
          className={cn(
            "rounded-md border-t border-x overflow-y-auto flex-grow",
            table.getState().columnSizingInfo.isResizingColumn &&
              "table-resizing",
          )}
          style={{
            maxWidth: isMobile
              ? "calc(100vw - 2rem)"
              : state === "collapsed"
                ? "calc(100vw - var(--sidebar-width-icon) - 2rem)"
                : "calc(100vw - var(--sidebar-width) - 2rem)",
            minWidth: "640px", // Prevent table from becoming too narrow on small screens
          }}
        >
          <Table
            className="w-full table-fixed"
            style={{ width: table.getTotalSize() }}
            data-table-id="webpages-table"
            data-table-container
          >
            {isLoading ? renderLoadingState() : renderTableContent()}
          </Table>
        </div>
      </DndContext>

      {/* Metric Drilldown Modal */}
      {selectedDrilldown && (
        <MetricDrilldown
          open={true}
          onOpenChange={(open) => !open && setSelectedDrilldown(null)}
          webpageUrl={selectedDrilldown.webpageUrl}
          metric={selectedDrilldown.metric}
          drillDownLimit={DRILLDOWN_LIMIT}
          channelId={
            selectedChannelId !== "all" ? selectedChannelId : undefined
          }
          metrics={
            webpages.find(
              (w) => w.url === selectedDrilldown.webpageUrl,
            ) as Partial<WebsitePageMetrics>
          }
          filters={{
            ...(hasAccountFilters && {
              account_filters: accountFilters,
            }),
            ...(hasOpportunityFilters && {
              opportunity_filters: opportunityFilters,
            }),
            ...(hasTouchpointFilters && {
              touchpoint_filters: touchpointFilters,
            }),
          }}
        />
      )}

      {/* Column Alias Modal */}
      <ColumnAliasModal
        open={showAliasModal}
        onOpenChange={setShowAliasModal}
        columnId={aliasColumnId}
        originalTitle={(() => {
          const column = columns.find((col) => col.key === aliasColumnId);
          return typeof column?.title === "string"
            ? column.title
            : aliasColumnId;
        })()}
        originalDescription={(() => {
          if (aliasColumnId.startsWith("metric_")) {
            const metricKey = aliasColumnId.replace("metric_", "");
            return METRIC_CONFIG[metricKey as keyof typeof METRIC_CONFIG]
              ?.tooltip;
          }
          return undefined;
        })()}
        currentAlias={columnAliases[aliasColumnId]}
        onSave={handleSaveAlias}
        onClear={handleClearAlias}
      />
    </Card>
  );
}
